import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { MarkerData } from '@bringg/react-components/dist/types/maps.consts';
import { action, makeObservable, observable, computed, runInAction } from 'mobx';
import { ExtendedMarkerData, ExtendedPolylineData } from '@bringg-frontend/bringg-map';
import { getRoot } from 'mobx-easy';

import { etaCalculatorProvider } from 'bringg-web/features/monitoring/shared/utils/eta-calculator/eta-calculator-provider';
import {
	generatePolyline,
	generateTaskMarker,
	generateTeamMarker
} from 'bringg-web/features/monitoring/shared/utils/map/map-utils';
import RootStore from 'bringg-web/stores/root-store';
import { orderExplorerRootStore } from './order-explorer-root-store';

class OrdersMapStore {
	markersToViewOnMap: Set<number | string> = new Set();

	routeDirection: Map<number, ExtendedPolylineData[]> = new Map();

	constructor() {
		makeObservable(this, {
			markersToViewOnMap: observable,
			routeDirection: observable,
			setMarkersToViewOnMap: action,
			toggleRouteOnMap: action,
			reset: action,
			markers: computed
		});
	}

	setMarkersToViewOnMap = (ids: (number | string)[], isChecked: boolean) => {
		runInAction(() => {
			ids.forEach(id => {
				if (isChecked) {
					this.markersToViewOnMap.add(id);
				} else {
					this.markersToViewOnMap.delete(id);
				}
			});
		});
	};

	toggleRouteOnMap = async (routeId: number, groupId: string, isChecked: boolean) => {
		if (!isChecked) {
			this.routeDirection.delete(routeId);
			return;
		}
		const res = await etaCalculatorProvider.getEtaCalculations([routeId]);

		const response = res[0];
		const { orderExplorerStore } = orderExplorerRootStore.getStore();

		if (response.status === 'fulfilled') {
			const color = orderExplorerStore.getItemColor(groupId);
			const line = generatePolyline(response.value, color);
			this.routeDirection.set(routeId, line);
		}
	};

	private getTeamMarkers = (teamIds: number[]): ExtendedMarkerData[] => {
		const { teamsStore } = getRoot<RootStore>().data;
		const ids = teamIds?.length ? teamIds : teamsStore.all.map(({ id }) => id);

		return ids
			.map(team_id => {
				const team = teamsStore.get(team_id);
				if (!team) {
					return undefined;
				}
				return generateTeamMarker(team);
			})
			.filter(Boolean);
	};

	get markers(): MarkerData[] {
		const { orderExplorerStore } = orderExplorerRootStore.getStore();

		const tasks = Array.from(this.markersToViewOnMap).map(id =>
			getRootEnv().dashboardSdk.sdk.v2.tasks.get(Number(id))
		);

		const tasksMarkers = tasks.map((task, index, allTasks) => {
			return generateTaskMarker(task, index, allTasks, orderExplorerStore.getItemColor(task.id));
		});

		let teamIds = orderExplorerStore.currentTableDefinition?.filters.task.selectedTeams;
		if (this.markersToViewOnMap.size) {
			teamIds = tasks.reduce((ids, _task) => {
				ids.push(..._task.team_ids);
				return ids;
			}, []);
		}

		const teamsMarkers = this.getTeamMarkers(teamIds);
		return [...teamsMarkers, ...tasksMarkers];
	}

	get polylines(): ExtendedPolylineData[] {
		return Array.from(this.routeDirection.values()).flat();
	}

	reset = () => {
		this.markersToViewOnMap.clear();
		this.routeDirection.clear();
	};
}

export default OrdersMapStore;
